import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessTaskStatusType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getProcedureUIString from 'procedures/ProceduresStringKeys';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as Colors from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Pane, {PaneBorder} from 'ui-library/pane/Pane';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as DateUtil from 'util/DateUtil';

import {Body, Cell, Header, HeaderCell, Row, Table, Title} from '../StudyTableComponent';
import {StudyInaccessibleContentBanner, StudyQueryErrorBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans as ProcedureRunOrPlanNode,
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans_ProcedureRunNode_processFlowchartTaskGroup_ProcessNode as ProcessFlowchartTaskGroup,
  MultiProcedureRunOrPlanListingTableQuery,
  MultiProcedureRunOrPlanListingTableQueryVariables,
} from './__generated/MultiProcedureRunOrPlanListingTableQuery';
import NoProcedureRunOverview from './NoProcedureRunOverview';
import {getProcedureRunOverview} from './ProcedureRunPlanUtils';
import {getProcedureRunPlanOverview} from './ProcedureRunPlanUtils';
import {RenderTaskStatusWithIcon} from './ProcedureRunTableUtil';

export const MULTI_PROCEDURE_RUN_OR_PLAN_LISTING_TABLE_QUERY: DocumentNode = gql`
  query MultiProcedureRunOrPlanListingTableQuery(
    $studyId: StrictString!
    $maxNumProcedureRunsOrPlans: StrictInt!
  ) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        activeProcedureRunsAndProcedureRunPlans(limit: $maxNumProcedureRunsOrPlans) {
          ... on ProcedureRunNode {
            id
            name
            description
            createdAt
            processFlowchartTaskGroup {
              ... on ProcessNode {
                id
                taskStatusCounts {
                  status {
                    id
                    statusType
                  }
                }
              }
            }
            # need to use alias to avoid conflict with the field in the ProcedureRunPlanNode
            runTemplateProcedureFlowchartVersion: templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            procedureFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
                procedure {
                  id
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
          ... on ProcedureRunPlanNode {
            id
            description
            createdAt
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      ... on ProcedureNode {
                        id
                        name
                      }
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            procedureFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
                procedure {
                  ... on ProcedureNode {
                    id
                    name
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcedureRunOverview {
  id: string;
  name: string;
  description: string;
  procedureTemplateId: string;
  createdAt: string;
  processFlowchartTaskGroup: ProcessFlowchartTaskGroup | null;
  flowchartVersionId?: string;
}

export interface ProcedureRunPlanOverview {
  id: string;
  name: string; // names of procedure run plans are the procedure name
  description: string;
  createdAt: string;
}

interface ProcedureRunAndPlanOverviewsAndInfo {
  procedureRunOverviews: Array<ProcedureRunOverview>;
  procedureRunPlanOverviews: Array<ProcedureRunPlanOverview>;
  hasInaccessibleProcedureRunsOrPlans: boolean;
}

function getProcedureRunAndPlanOverviews(
  procedureRunOrPlans: Array<ProcedureRunOrPlanNode>
): ProcedureRunAndPlanOverviewsAndInfo {
  const procedureRunOverviews: Array<ProcedureRunOverview> = [];
  const procedureRunPlanOverviews: Array<ProcedureRunPlanOverview> = [];
  for (const procedureRunOrPlan of procedureRunOrPlans) {
    if (isInaccessible(procedureRunOrPlan)) {
      continue;
    }
    if (procedureRunOrPlan.__typename === 'ProcedureRunNode') {
      const overview: ProcedureRunOverview | null = getProcedureRunOverview(procedureRunOrPlan);
      if (overview != null) {
        procedureRunOverviews.push(overview);
      }
    } else if (procedureRunOrPlan.__typename === 'ProcedureRunPlanNode') {
      const overview: ProcedureRunPlanOverview | null = getProcedureRunPlanOverview(procedureRunOrPlan);
      if (overview != null) {
        procedureRunPlanOverviews.push(overview);
      }
    }
  }
  return {
    procedureRunOverviews,
    procedureRunPlanOverviews,
    hasInaccessibleProcedureRunsOrPlans:
      procedureRunOverviews.length + procedureRunPlanOverviews.length < procedureRunOrPlans.length,
  };
}

interface MultiProcedureRunParamsTableProps {
  studyId: string;
}

export default function MultiProcedureRunAndPlanListingTable({
  studyId,
}: MultiProcedureRunParamsTableProps): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunOrPlanListingTableQuery,
    MultiProcedureRunOrPlanListingTableQueryVariables
  >(MULTI_PROCEDURE_RUN_OR_PLAN_LISTING_TABLE_QUERY, {
    variables: {studyId, maxNumProcedureRunsOrPlans: BenchlingConfig.STUDIES_MAX_NUM_RUNS_PER_PD_STUDY},
    fetchPolicy: 'cache-and-network',
  });

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredSpinner />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  }
  const {data} = result;
  if (!data) {
    return <StudyQueryErrorBanner />;
  }
  if (isInaccessible(data.study)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.study}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
      />
    );
  }
  const {procedureRunOverviews, procedureRunPlanOverviews, hasInaccessibleProcedureRunsOrPlans} =
    getProcedureRunAndPlanOverviews(data.study.activeProcedureRunsAndProcedureRunPlans);
  return (
    <>
      {hasInaccessibleProcedureRunsOrPlans && (
        <div className={css(styles.inaccessibleProcedureRunBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text={`One or more ${getProcedureUIString(
              ProceduresStringKeys.PROCEDURE,
              getProcedureTypeForStudyType(data.study.studySchema.studyType),
              {stringCase: StringCase.LOWER}
            )} runs ${
              BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN
                ? `or ${getProcedureUIString(
                    ProceduresStringKeys.PROCEDURE,
                    getProcedureTypeForStudyType(data.study.studySchema.studyType),
                    {stringCase: StringCase.LOWER}
                  )} run plans`
                : ''
            } are hidden. Request access from your admin to view.`}
          />
        </div>
      )}
      <RenderMultiProcedureRunListingTable
        studyId={studyId}
        studyType={data.study.studySchema.studyType}
        procedureRuns={procedureRunOverviews}
        procedureRunPlans={procedureRunPlanOverviews}
      />
    </>
  );
}

// exported for testing
export function getProcedureRunStatusComponent({
  processFlowchartTaskGroup,
}: {
  processFlowchartTaskGroup: ProcessFlowchartTaskGroup | null;
}): JSX.Element | string {
  if (processFlowchartTaskGroup == null) {
    return '';
  }
  const hasFailed = processFlowchartTaskGroup.taskStatusCounts.some(
    (task) => task.status.statusType === ProcessTaskStatusType.FAILED
  );
  if (hasFailed) {
    return <RenderTaskStatusWithIcon statusType={ProcessTaskStatusType.FAILED} />;
  }
  const isInProgress = processFlowchartTaskGroup.taskStatusCounts.some(
    (task) =>
      task.status.statusType === ProcessTaskStatusType.PENDING ||
      task.status.statusType === ProcessTaskStatusType.IN_PROGRESS
  );
  return (
    <RenderTaskStatusWithIcon
      statusType={isInProgress ? ProcessTaskStatusType.IN_PROGRESS : ProcessTaskStatusType.COMPLETED}
    />
  );
}

interface RenderMultiProcedureRunParamsTableProps {
  studyId: string;
  studyType: StudyType;
  procedureRuns: Array<ProcedureRunOverview>;
  procedureRunPlans: Array<ProcedureRunPlanOverview>;
}

// Renders the description inside a Cell and shows a tooltip only when the text is truncated.
function DescriptionCellWithTooltip({description}: {description: string | null}): JSX.Element {
  const descriptionText = description ?? '';
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isEllipsized, setIsEllipsized] = React.useState(false);

  React.useLayoutEffect(() => {
    if (textRef.current != null) {
      setIsEllipsized(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [descriptionText]);

  return (
    <Cell className={css(styles.descriptionCell)}>
      <Tooltip
        text={descriptionText}
        placement={FloatingPlacement.TOP}
        isDisabled={!isEllipsized}
        toggleDisplayMode={ToggleDisplayMode.BLOCK_FIT_CONTENT}
      >
        <div ref={textRef} className={css(styles.descriptionCell)}>
          {descriptionText}
        </div>
      </Tooltip>
    </Cell>
  );
}

export function RenderMultiProcedureRunListingTable({
  studyId,
  studyType,
  procedureRuns,
  procedureRunPlans,
}: RenderMultiProcedureRunParamsTableProps): JSX.Element {
  const procedureRunTypeSpecificTitle = `${getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    getProcedureTypeForStudyType(studyType),
    {stringCase: StringCase.SENTENCE}
  )} runs`;
  return procedureRuns.length === 0 && procedureRunPlans.length === 0 ? (
    // Pane has a hardcoded flex-grow: 1, so we need to wrap it in a div to prevent it from taking up the full height
    <div>
      <Pane
        title={procedureRunTypeSpecificTitle}
        paneBorder={PaneBorder.SOLID}
        fitContent={true}
        dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'title'}}
      >
        <div className={css(styles.noExperimentViewInnerContainer)}>
          <NoProcedureRunOverview studyId={studyId} studyType={studyType} />
        </div>
      </Pane>
    </div>
  ) : (
    <Table
      dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'table'}}
      scrollContents={true}
    >
      <Title dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'title'}}>
        {procedureRunTypeSpecificTitle}
      </Title>
      <Header className={css(styles.headerRowStyle)}>
        <HeaderCell dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'headerCell-name'}}>
          Name
        </HeaderCell>
        <HeaderCell
          className={css(styles.headerstatusCell)}
          dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'headerCell-status'}}
        >
          Status
        </HeaderCell>
        <HeaderCell
          className={css(styles.headerCreatedAtCell)}
          dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'headerCell-created'}}
        >
          Created
        </HeaderCell>
        <HeaderCell
          className={css(styles.headerDescriptionCell)}
          dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'headerCell-description'}}
        >
          Description
        </HeaderCell>
      </Header>
      <Body>
        {procedureRunPlans.map((procedureRunPlan) => (
          <Row
            key={procedureRunPlan.id}
            dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'row'}}
          >
            <Cell
              className={css(styles.procedureRunPlanNameCell)}
              dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'planNameCell'}}
            >
              {procedureRunPlan.name}
              <div
                className={css(styles.planLabel)}
                {...getTestProps({component: 'MultiProcedureRunListingTable', element: 'planLabel'})}
              >
                Plan
              </div>
            </Cell>
            <Cell className={css(styles.procedureRunPlanStatusCell)}>N/A</Cell>
            <Cell className={css(styles.headerCreatedAtCell)}>
              <div className={css(styles.createdAtCell)}>
                {DateUtil.readableTime(procedureRunPlan.createdAt, {showTime: true})}
              </div>
            </Cell>
            <DescriptionCellWithTooltip description={procedureRunPlan.description} />
          </Row>
        ))}
        {procedureRuns.map((procedureRun) => (
          <Row
            key={procedureRun.id}
            onClick={() => {
              navigateToStudyOverviewTab(studyId, procedureRun.id);
            }}
            className={css(styles.rowStyle)}
            dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'row'}}
          >
            <Cell
              className={css(styles.nameCell)}
              dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'nameCell'}}
            >
              {procedureRun.name}
            </Cell>
            <Cell className={css(styles.statusCell)}>
              {getProcedureRunStatusComponent({
                processFlowchartTaskGroup: procedureRun.processFlowchartTaskGroup,
              })}
            </Cell>
            <Cell className={css(styles.headerCreatedAtCell)}>
              <div className={css(styles.createdAtCell)}>
                {DateUtil.readableTime(procedureRun.createdAt, {showTime: true})}
              </div>
            </Cell>
            <DescriptionCellWithTooltip description={procedureRun.description} />
          </Row>
        ))}
      </Body>
    </Table>
  );
}

const styles = StyleSheet.create({
  createdAtCell: {
    maxWidth: 198,
    minWidth: 158,
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  nameCell: {
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  procedureRunPlanNameCell: {
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    alignItems: 'center',
  },
  statusCell: {maxWidth: 158, minWidth: 118, alignContent: 'center', ...FONT_PROXIMA_13},
  loadingDiv: {paddingBottom: 5},
  rowStyle: {width: '100%', minHeight: 44},
  headerRowStyle: {height: 32, alignContent: 'center', color: Colors.GRAY_SEMIDARK, ...FONT_PROXIMA_13},
  headerCreatedAtCell: {maxWidth: 198, minWidth: 158},
  headerDescriptionCell: {maxWidth: 278, minWidth: 198},
  headerstatusCell: {maxWidth: 158, minWidth: 118},
  procedureRunPlanStatusCell: {
    maxWidth: 158,
    minWidth: 118,
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    color: Colors.GRAY_DARK,
  },
  noExperimentViewInnerContainer: {backgroundColor: Color.GRAY_100, padding: '40px 0px 40px 0px'},
  inaccessibleProcedureRunBanner: {padding: 4, backgroundColor: Color.WHITE},
  descriptionCell: {
    maxWidth: 278,
    minWidth: 198,
    ...FONT_PROXIMA_13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  planLabel: {
    background: Color.GRAY_100,
    color: Color.GRAY_800,
    fontSize: 12,
    fontWeight: 600,
    marginLeft: 'auto',
    marginRight: 10,
    padding: '2px 6px',
    width: 'fit-content',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
  },
});
